import { useRef, useState } from 'react'
import { Slider } from './Slider'

interface Props {
  src: string
}

export function AudioPlayer({ src }: Props) {
  const [playing, setPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const audioRef = useRef<HTMLAudioElement>(null)

  const toggle = () => {
    if (!audioRef.current) return
    if (playing) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setPlaying(!playing)
  }

  const safeDuration = isFinite(duration) && duration > 0 ? duration : 0
  const remaining = Math.max(0, safeDuration - currentTime)
  const displaySecs = playing ? remaining : safeDuration
  const mins = Math.floor(displaySecs / 60)
  const secs = Math.floor(displaySecs % 60).toString().padStart(2, '0')

  return (
    <div className="flex items-center gap-2 min-w-[180px] py-1">
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
        onEnded={() => { setPlaying(false); setCurrentTime(0) }}
      />
      <button
        onClick={toggle}
        className="w-8 h-8 rounded-full bg-accent/20 flex items-center justify-center shrink-0 hover:bg-accent/30 transition-colors"
        aria-label={playing ? 'Pause' : 'Play'}
      >
        {playing ? (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16"/>
            <rect x="14" y="4" width="4" height="16"/>
          </svg>
        ) : (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5,3 19,12 5,21"/>
          </svg>
        )}
      </button>
      <Slider
        min={0}
        max={duration || 1}
        step={0.1}
        value={currentTime}
        onChange={(v) => { if (audioRef.current) audioRef.current.currentTime = v }}
        className="flex-1"
      />
      <span className="text-txt-meta text-xs shrink-0 tabular-nums">
        {safeDuration > 0 ? `${mins}:${secs}` : '--:--'}
      </span>
    </div>
  )
}
